import { useEffect, useState, useMemo } from "react";
import { SAlert, SPagination, SIcon } from "@avalara/skylab-sdk/react";
import {
  PaginationType,
  deactivateTemplatesBySchemaId,
  getSchemasBySchemaId,
  getTemplatesByTenantId,
} from "../../services/schemaService";
import { MappingContext } from "../../context/mappingDataContext";
import { ISchema, Snapshot } from "../../domain/mappingTypes";
import LoadingMask from "../../ui/LoadingMask/LoadingMask";
import TemplateTableAvalara from "./TemplateAvalaraTable";
import SlideDownEffect from "../../ui/SlideDownEffect/SlideDownEffect";
import ConfirmDialog from "./ConfirmDialog";
import ModalEntrance from "../../ui/ModalEntrance/ModalEntrance";
import { useLocation, useNavigate } from "react-router-dom";
import FadeInEffect from "../../ui/FadeInEffect/FadeInEffect";

export type TPagination = {
  startIndex: number;
  endIndex: number;
  currentPage: number;
  rowsPerPage: number;
  flag: string;
  sort: string;
  order: string;
};

const errorMessageFetch =
  "An error occurred when trying to get template list. Please check your internet and vpn conection.";
const errorMessageDeactivate = "An error occurred on delete";
const successMessageDeactivate = "Template deleted successfully";

const ConfirmMessageDeactivate: React.FunctionComponent<{ schemaName: string }> = (props) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <div style={{ marginLeft: "30px", marginRight: "20px" }}>
      <SIcon name="alert-triangle-filled" />
    </div>
    <div style={{ fontSize: "18px", fontStyle: "italic", wordBreak: "break-all" }}>
      Are you sure you want to delete the template{" "}
      <span>
        <b>"{props.schemaName}"</b>
      </span>
      {""}?
    </div>
  </div>
);

const SchemasList = () => {
  const navigate = useNavigate();

  const {
    populateEditMode,
    dataComponent: { tenantId },
  } = MappingContext();
  const location = useLocation();

  const [templates, setTemplates] = useState<ISchema[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [schemaId, setSchemaId] = useState<string>("");
  const [schemaName, setSchemaName] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pagination, setPagination] = useState<TPagination>({
    startIndex: 0,
    endIndex: 0,
    currentPage: 1,
    rowsPerPage: 10,
    flag: "",
    sort: "schemaInsertedDate",
    order: "Desc",
  });
  const [savedTemplateName, setSavedTemplateName] = useState(
    sessionStorage.getItem("saveSuccess") ?? ""
  );

  const { rowsPerPage, startIndex, sort, order } = pagination;

  const fetchTemplates = useMemo(
    () => async () => {
      const paginationInfo: PaginationType = {
        take: rowsPerPage,
        skip: startIndex,
        sort: sort,
        order: order,
      };
      try {
        setIsLoading(true);
        const result = await getTemplatesByTenantId(tenantId, paginationInfo);
        setTemplates(result.values);
        setTotalRecords(result.count);
      } catch (error) {
        setError(true);
        setErrorMessage(errorMessageFetch);
        console.error("Error fetching templates:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId, rowsPerPage, startIndex, sort, order]
  );

  useEffect(() => {
    if (savedTemplateName) {
      const timer = setTimeout(() => {
        setSavedTemplateName("");
      }, 5000);

      return () => clearTimeout(timer);
    }
    sessionStorage.removeItem("saveSuccess");
  }, [savedTemplateName]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const deactivateHandler = async () => {
    try {
      setIsLoading(true);
      await deactivateTemplatesBySchemaId(schemaId);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      fetchTemplates();
    } catch (error) {
      setError(true);
      setErrorMessage(errorMessageDeactivate);
      setIsLoading(false);
      console.error("Error deactivating templates", error);
    } finally {
      onDialogDismiss();
    }
  };

  const showConfirmHandler = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    schemaId: string,
    schemaName: string
  ) => {
    event.currentTarget.blur();
    setShowDialog(true);
    setSchemaId(schemaId);
    setSchemaName(schemaName);
  };

  const onDialogDismiss = () => {
    setShowDialog(false);
    setSchemaId("");
    setSchemaName("");
  };

  const handleEdit = async (schemaId: string) => {
    try {
      setIsLoadingEdit(true);
      const schema = await getSchemasBySchemaId(schemaId);
      const snapshot: Snapshot = JSON.parse(schema.snapshot);
      snapshot.dataComponent.schemaId = schema.schemaId;
      populateEditMode(snapshot);
      navigate(`/${location.search}`);
    } catch (error) {
      console.error("Error fetching schema data:", error);
      setError(true);
      setErrorMessage("An error occurred while fetching schema data. Please try again.");
    } finally {
      setIsLoadingEdit(false);
    }
  };

  return (
    <>
      <h2>Templates</h2>

      {isLoadingEdit && <LoadingMask />}

      <SlideDownEffect showElement={error} id="ErrorMessage">
        <SAlert
          status="error"
          data-testid="error-message"
          onS-dismiss={(e) => setError(false)}
          className="margin-bottom-md"
        >
          <div>{errorMessage}</div>
        </SAlert>
      </SlideDownEffect>

      <SlideDownEffect showElement={success} id="SuccessMessage">
        <SAlert
          data-testid="success-message"
          status="success"
          onS-dismiss={(e) => setSuccess(false)}
          className="margin-bottom-md"
        >
          <div>{successMessageDeactivate}</div>
        </SAlert>
      </SlideDownEffect>

      <ModalEntrance id="confirm-dialog" showElement={showDialog} duration={0.2}>
        <ConfirmDialog
          title="Confirm"
          content={<ConfirmMessageDeactivate schemaName={schemaName} />}
          showDialog={showDialog}
          onConfirm={deactivateHandler}
          onCancel={onDialogDismiss}
        />
      </ModalEntrance>

      <div className="s-table-container" style={{ minHeight: "348px" }}>
        {isLoading && <LoadingMask topSpace="45px" />}
        <TemplateTableAvalara
          templateList={templates}
          onChangeSort={setPagination}
          onShowConfirm={showConfirmHandler}
          onEdit={handleEdit}
        />
      </div>

      <SPagination
        className="margin-top-sm"
        disabled={isLoading}
        totalRecords={totalRecords}
        perPageLabel={"Elements per page"}
        onS-paginate={(e) =>
          setPagination((prevState) => {
            return { ...prevState, ...e.detail };
          })
        }
      />
      <FadeInEffect showElement={!!savedTemplateName} id="sucessSavedMessage">
        <SAlert
          data-testid="sucess-saved-message"
          className="margin-top-lg"
          status="success"
          onS-dismiss={() => setSavedTemplateName("")}
        >
          <div>Template {savedTemplateName} saved successfully.</div>
        </SAlert>
      </FadeInEffect>
    </>
  );
};

export default SchemasList;
